'use client';

import { useEffect } from 'react';

/**
 * Interpolated scrolling for the marketing pages.
 *
 * This is the single highest-value motion change on the site per kilobyte.
 * Scroll-triggered reveals, a drawn timeline and a section rail all read as
 * deliberate when the scroll they are tied to eases; against a stepped native
 * scroll the same effects read as jitter. It is also the one thing every
 * "expensive-feeling" site has in common and no amount of styling substitutes
 * for.
 *
 * Three things it deliberately does not do:
 *
 *  - It does not run for anyone who asked for reduced motion. Smooth scrolling
 *    is a vestibular trigger, and hijacking the scroll is the most disorienting
 *    thing a page can do to someone susceptible to it.
 *  - It does not run on touch devices. Native momentum scrolling on a phone is
 *    already smooth, better tuned than anything we would ship, and overriding
 *    it costs battery and breaks pull-to-refresh.
 *  - It does not touch anchor links. `scroll-behavior: smooth` in the
 *    stylesheet still handles those, and Lenis respects it.
 */
export default function SmoothScroll() {
  useEffect(() => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const coarse = window.matchMedia('(pointer: coarse)');
    if (reduced.matches || coarse.matches) return;

    let raf = 0;
    let lenis: { raf: (t: number) => void; destroy: () => void } | null = null;
    let cancelled = false;

    // Imported at run time so the library is never in the initial bundle for
    // the visitors who will not use it — phones, and anyone with the OS
    // preference set.
    void import('lenis').then(({ default: Lenis }) => {
      if (cancelled) return;
      lenis = new Lenis({
        // Slightly under the default. Long enough to feel eased, short enough
        // that the page still lands where the flick aimed it.
        duration: 0.9,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
        touchMultiplier: 1,
      });

      const tick = (time: number) => {
        lenis?.raf(time);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      lenis?.destroy();
    };
  }, []);

  return null;
}
